import { defineStore } from 'pinia'
import { fcmKeyPair, fcmServerKey } from '../runtime-config'
import { useUserStore } from '../stores/user'
import { useFirebase } from '../services/firebase'
import { closeSession } from '../services/auth'
import {
  //Auth utils
  getAuth,
  onAuthStateChanged,
  updateProfile,
  signOut,
  //for google login/register)
  GoogleAuthProvider,
  signInWithPopup,
  //for email & pass login/register
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendEmailVerification,
  sendPasswordResetEmail,
  updatePassword,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
  RecaptchaVerifier,
  signInWithPhoneNumber,
  //for appleId login/register
} from 'firebase/auth'

export const useFirebaseStore = defineStore('firebase', () => {
  const { auth } = useFirebase()
  const userX = useUserStore()
  auth.languageCode = 'es'
  let recaptchaVerifier: RecaptchaVerifier | null = null
  let phoneConfirmationResult
  function sendResponse(status: boolean, data: any) {
    return { success: status, data: data }
  }

  function listenAuthChanges() {
    onAuthStateChanged(
      auth,
      async (user) => {
        if (user) {
          userX.setFirebaseUser(user)
        } else {
          //Have to Logout
          userX.setFirebaseUser(null)
        }
      },
      () => {
        //Have to Logout
        userX.setFirebaseUser(null)
      },
    )
  }

  /*

  async function requestPermission() {
    if (checkApiUserInterval.value === null) {
      checkApiUserInterval.value = setInterval(async () => {
        if (userX.isLoggedIn) {
          const permission = await Notification.requestPermission();

          console.log(permission);

          if (permission === "granted") {
            console.log("Notification permission granted.");
            loadToken();
          } else {
            console.log("Do not have permission!");
          }

          console.log("interval val", checkApiUserInterval.value);

          clearInterval(checkApiUserInterval.value);
        } else {
          console.log("userX not logged in");
        }
      }, 5000);
    }
  }

  async function loadToken() {
    const messaging = getMessaging();
    let KEY_PAIR = fcmKeyPair();

    getToken(messaging, { vapidKey: KEY_PAIR })
      .then(async (currentToken) => {
        if (currentToken) {
          console.log("client token", currentToken);
          await subscribeTokenToTopic(currentToken, "fcm_admin");

          const { useRequesterStore } = await import("@/stores/requester");

          const requesterX = useRequesterStore();

          let send = {
            fcmToken: currentToken,
          };

          let check = await requesterX.Post({
            route: "/fcm-token",
            withAuth: true,
            body: send,
          });

          if (check.status === 200) {
            console.log("Done save or update token");
          }
        } else {
          console.log(
            "No registration token available. Request permission to generate one."
          );
        }
      })
      .catch((err) => {
        console.log("An error occurred while retrieving token. ", err);

        // ? commented due to recursive errors
        // loadToken();
      });

    onMessage(messaging, async (payload) => {
      const { useUtilsStore } = await import("@/stores/utils");

      const utilsX = useUtilsStore();

      let notification = payload.notification;
      console.log(notification);

      utilsX.setNotif({
        title: notification.title,
        message: notification.body,
        type: "info",
        timeVisible: 5,
        position: "top-center",
      });
    });
  }

  async function subscribeTokenToTopic(token, topic) {
    let SERVER_KEY = fcmServerKey();
    let API =
      "https://iid.googleapis.com/iid/v1/" + token + "/rel/topics/" + topic;
    fetch(API, {
      method: "POST",
      headers: new Headers({
        Authorization: "key=" + SERVER_KEY,
      }),
    })
      .then((response) => {
        if (response.status < 200 || response.status >= 400) {
          throw (
            "Error subscribing to topic: " +
            response.status +
            " - " +
            response.text()
          );
        }
        console.log('Subscribed to "' + topic + '"');
      })
      .catch((error) => {
        console.error({ error });
      });
  }*/

  async function login(email: string, password: string, keepLoggedIn: boolean) {
    try {
      const persistenceType = keepLoggedIn ? browserLocalPersistence : browserSessionPersistence
      await setPersistence(auth, persistenceType)
      await signInWithEmailAndPassword(auth, email, password)
      const user = auth.currentUser
      return sendResponse(true, { user })
    } catch (error: any) {
      console.error(error)
      switch (error.code) {
        case 'auth/user-not-found':
          return sendResponse(false, { msg: 'error.userNotFound' })
        case 'auth/wrong-password':
          return sendResponse(false, { msg: 'error.wrongPassword' })
        case 'auth/invalid-email':
          return sendResponse(false, { msg: 'error.invalidEmail' })

        default:
          return sendResponse(false, { msg: 'error.something' })
      }
    }
  }

  async function googleLogin(keepLoggedIn: boolean) {
    try {
      const persistenceType = keepLoggedIn ? browserLocalPersistence : browserSessionPersistence
      await setPersistence(auth, persistenceType)
      const provider = new GoogleAuthProvider()
      await signInWithPopup(auth, provider)
      const user = auth.currentUser
      return sendResponse(true, { user, msg: 'auth.loginSuccess' })
    } catch (error) {
      console.log(error)
      return sendResponse(false, { msg: 'error.something' })
    }
  }

  async function register(email: string, password: string, name: string) {
    try {
      await createUserWithEmailAndPassword(auth, email, password)
      const user = auth.currentUser
      await updateProfile(user!, {
        displayName: name,
      })

      sendConfirmationCode()
      await logout()
      return {
        success: true,
        data: {
          msg: '',
        },
      }
    } catch (error: any) {
      console.log(error)
      switch (error.code) {
        case 'auth/email-already-in-use':
          return sendResponse(false, { msg: 'error.emailUsed' })
        case 'auth/invalid-email':
          return sendResponse(false, { msg: 'error.invalidEmail' })
        case 'auth/weak-password':
          return sendResponse(false, { msg: 'error.weakPassword' })

        default:
          return sendResponse(false, { msg: 'error.something' })
      }
    }
  }

  async function logout() {
    try {
      //close api session
      await closeSession()
      //remove data from localstorage
      userX.setApiUser(null)
      //close firebase session
      await signOut(auth)
      return sendResponse(true, { msg: 'auth.logoutSuccess' })
    } catch (error) {
      console.log(error)
      return sendResponse(false, { msg: 'error.something' })
    }
  }

  async function sendConfirmationCode() {
    try {
      const user = auth.currentUser
      await sendEmailVerification(user!)
      return sendResponse(true, { msg: 'auth.verificationSended' })
    } catch (error) {
      console.log(error)
      return sendResponse(false, { msg: 'error.something' })
    }
  }

  async function recover(email: string) {
    try {
      await sendPasswordResetEmail(auth, email)
      return sendResponse(true, { msg: 'auth.recoverSended' })
    } catch (error: any) {
      switch (error.code) {
        case 'auth/user-not-found':
          return sendResponse(false, { msg: 'error.userNotFound' })
        case 'auth/invalid-email':
          return sendResponse(false, { msg: 'error.invalidEmail' })

        default:
          return sendResponse(false, { msg: 'error.something' })
      }
    }
  }

  async function passwordUpdate(password: string) {
    try {
      const user = auth.currentUser
      await updatePassword(user!, password)
      return sendResponse(true, { msg: 'auth.changingPasswordSuccess' })
    } catch (error) {
      console.log(error)
      return sendResponse(false, { msg: 'error.something' })
    }
  }

  function initRecaptcha() {
    if (!recaptchaVerifier) {
      recaptchaVerifier = new RecaptchaVerifier(
        auth,
        'recaptcha-container', // ID del div
        { size: 'invisible' }, // puede ser 'normal' para mostrarlo
      )
    }
  }
  async function phoneLogin(phone: string) {
    try {
      initRecaptcha()
      phoneConfirmationResult = await signInWithPhoneNumber(auth, phone, recaptchaVerifier!)
    } catch (err: any) {
      console.log(err)
    }
  }
  async function verifyCode(code: string, keepLoggedIn: boolean) {
    try {
      const persistenceType = keepLoggedIn ? browserLocalPersistence : browserSessionPersistence
      await setPersistence(auth, persistenceType)
      await phoneConfirmationResult!.confirm(code)
      const user = auth.currentUser
      return sendResponse(true, { user })
    } catch (error: any) {
      switch (error.code) {
        case 'auth/invalid-verification-code':
          return sendResponse(false, { msg: 'error.verificationError' })
        default:
          return sendResponse(false, { msg: 'error.something' })
      }
    }
  }

  return {
    login,
    googleLogin,
    register,
    logout,
    sendConfirmationCode,
    listenAuthChanges,
    recover,
    passwordUpdate,
    phoneLogin,
    verifyCode,
  }
})
